<template>
  <div>
    <v-card outlined class="mb-2 pa-3">
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-col cols="12">
          <v-row>
            <v-col cols="8" v-if="!editMode">
              <h3>{{ titrationScheme.name }}</h3>
            </v-col>
            <v-col v-if="editMode">
              <v-text-field
                  v-model="tempData.name"
                  outlined
                  :label="$ucfirst($t('myWeekMedicationDetail.name', { catalog: 'app_mobile' }))"
                  required
                  dense
                  :rules="nameRules"
              ></v-text-field>
            </v-col>
            <v-col cols="4" align="right" v-if="!readonly">
              <v-btn
                class="mr-3"
                fab
                text
                color="primary"
                @click="editScheme()"
                v-if="!editMode"
              >
                <eva-icon name="edit-2-outline"></eva-icon>
              </v-btn>
              <v-btn
                class="mr-3"
                fab
                text
                color="primary"
                @click="saveScheme()"
                :disabled="!valid"
                v-if="editMode"
              >
                <eva-icon name="save-outline"></eva-icon>
              </v-btn>
              <v-btn
                fab
                text
                color="error"
                @click="deleteScheme()"
              >
                <eva-icon fill="red" name="trash-2-outline"></eva-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-row v-if="!editMode">
            <v-col cols="2">{{ $ucfirst($t("dss.startDosage")) }}</v-col>
            <v-col
            >
              {{ titrationScheme.startDosage.doseAndRate[0].doseQuantity.value }}
              {{ translateUnit(titrationScheme.startDosage.doseAndRate[0].doseQuantity.code) }}
            </v-col
            >
          </v-row>
          <v-row v-if="editMode">
            <v-col cols="2">
              <p class="pt-5 mb-0">
                {{ $ucfirst($t("dss.startDosage")) }}
              </p>
            </v-col>
            <v-col>
              <v-row>
                <v-col cols="2">
                  <v-text-field
                      v-model="tempData.startDosage.value"
                      outlined
                      :label="$ucfirst($t('myWeekMedicationDetail.dosage', { catalog: 'app_mobile' }))"
                      required
                      dense
                      type="number"
                      :rules="numberRules"
                  ></v-text-field>
                </v-col>
                <v-col cols="2">
                  <v-select
                      :items="unitsToSelect"
                      item-text="label"
                      item-value="value"
                      outlined
                      v-model="tempData.startDosage.code"
                      :label="$ucfirst($t('dss.unit'))"
                      required
                      dense
                  ></v-select>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row v-if="!editMode">
            <v-col cols="2">{{ $ucfirst($t("dss.targetDosage")) }}</v-col>
            <v-col
            >
              {{ titrationScheme.endDosage.doseAndRate[0].doseQuantity.value }}
              {{ translateUnit(titrationScheme.endDosage.doseAndRate[0].doseQuantity.code) }}
            </v-col
            >
          </v-row>
          <v-row v-if="editMode">
            <v-col cols="2">
              <p class="pt-5 mb-0">
                {{ $ucfirst($t("dss.targetDosage")) }}
              </p>
            </v-col>
            <v-col>
              <v-row>
                <v-col cols="2">
                  <v-text-field
                      v-model="tempData.endDosage.value"
                      outlined
                      :label="$ucfirst($t('myWeekMedicationDetail.dosage', { catalog: 'app_mobile' }))"
                      required
                      dense
                      type="number"
                      :rules="numberRules"
                  ></v-text-field>
                </v-col>
                <v-col cols="2">
                  <v-select
                      :items="unitsToSelect"
                      item-text="label"
                      item-value="value"
                      outlined
                      v-model="tempData.endDosage.code"
                      :label="$ucfirst($t('dss.unit'))"
                      required
                      dense
                  ></v-select>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row v-if="!editMode">
            <v-col cols="2">{{ $ucfirst($t("dss.description")) }}</v-col>
            <v-col>{{ titrationScheme.description || $t('dss.noDescriptionAdded') }}</v-col>
          </v-row>
          <v-row v-if="editMode">
            <v-col cols="2">
              <p class="pt-2 mb-0">
                {{ $ucfirst($t("dss.description")) }}
              </p>
            </v-col>
            <v-col>
              <v-text-field
                  v-model="tempData.description"
                  outlined
                  :label="$ucfirst($t('dss.description'))"
                  required
                  dense
              ></v-text-field>
            </v-col>
          </v-row>
        </v-col>
      </v-form>
    </v-card>

    <v-dialog
        v-model="showDialog"
        width="600px"
    >
      <div class="dashboard-dialog-window">
        <div class="dashboard-dialog-window__message">
          {{
            this.$ucfirst(
                this.$t("dss.areYouSureToDeleteTitrationScheme", {
                  params: {
                    titrationSchemeName:
                        this.tempData.name ||
                        this.$ucfirst(
                            this.$t("dss.noName")
                        ),
                  },
                })
            )
          }}
        </div>
        <div class="dashboard-dialog-window__actions">
          <v-btn
              x-large
              text
              class="ml-4"
              @click="deleteScheme"
          >
            {{ $ucfirst($t('confirm')) }}
          </v-btn>
          <v-btn
              x-large
              text
              class="ml-4"
              @click="showDialog = false"
              color="primary"
          >
            {{ $ucfirst($t('buttons.cancel', {catalog: 'app_mobile'})) }}
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import {UCUMUnit} from "../../../../../helper/UCUMUnit";

/**
 * Component DosageTitrationSchemeCard.vue
 *
 * @module DosageTitrationSchemeCard
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.10] app/src/APP_DASHBOARD/templates/components/Parts/DSS/DosageTitrationSchemeCard.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: "DosageTitrationSchemeCard",
  props: {
    titrationScheme: {
      type: Object,
    },
    readonly: {
      type: Boolean,
      default: false,
    }
  },
  data: function () {
    return {
      showDialog: false,
      valid: true,
      editMode: false,
      tempData: {},
      unitsToSelect:
          UCUMUnit.generateTranslationLabelValueObjectsWithCatalogForUnits(
              this.$t,
              'label',
              UCUMUnit.UG,
              UCUMUnit.MG,
              UCUMUnit.G,
              UCUMUnit.UL,
              UCUMUnit.MG_ML,
              UCUMUnit.CL,
              UCUMUnit.L,
              UCUMUnit.ML,
              UCUMUnit.IU,
              UCUMUnit.MG_IU,
              UCUMUnit.OTHER,
              UCUMUnit.UNKNOWN,
          )
      ,
      nameRules: [
        (v) => !!v || this.$ucfirst(this.$t("validation.IS_REQUIRED")),
      ],
      numberRules: [
        (v) => {
          if ((v || "").toString().includes("e")) {
            return this.$ucfirst(this.$t("validation.onlyNumbersAreAllowed"));
          }
          if (!+v) return this.$ucfirst(this.$t("validation.IS_REQUIRED"));
          if (v < 0) return this.$ucfirst(this.$t("validation.onlyPositiveNumbers"));
          return true
        },
      ],
    };
  },
  mounted: function () {
    this.tempData = {
      type: "DOSAGE",
      id: this.titrationScheme.id,
      name: this.titrationScheme.name,
      description: this.titrationScheme.description,
      startDosage: {
        value:
        this.titrationScheme.startDosage.doseAndRate[0].doseQuantity.value,
        code: this.titrationScheme.startDosage.doseAndRate[0].doseQuantity.code,
      },
      endDosage: {
        value: this.titrationScheme.endDosage.doseAndRate[0].doseQuantity.value,
        code: this.titrationScheme.endDosage.doseAndRate[0].doseQuantity.code,
      },
    };
    this.setInputWatchers();
  },
  methods: {
    updateDoseCodeTranslationIfNeeded(code) {
      return new UCUMUnit(code).updateDoseCodeTranslationIfNeeded();
    },
    translateUnit(code) {
      return new UCUMUnit.FromCode(code).translate(this.$t);
    },
    saveScheme() {
      if (this.$refs.form.validate()) {
        this.$emit("save-scheme", this.tempData);
        this.editMode = false;
      } else {
        console.log("THERE ARE ERRORS IN THE FORM");
      }
    },
    editScheme() {
      this.editMode = true;
    },
    deleteScheme() {
      if (!this.showDialog) {
        this.showDialog = true;
      } else {
        this.$emit("delete-scheme", this.$props.titrationScheme.id);
        this.showDialog = false;
      }
    },
    setInputWatchers() {
      const keys = Object.keys(this.tempData);
      keys.forEach((key) => {
        this.$watch(`tempData.${key}`, {
          deep: true,
          handler: function() {
            const data = {
              unsavedChanges: true,
              screen: 'titrationSchemes',
              name: `addTitrationScheme-${this.tempData.id}`
            }
            this.$store.dispatch('patientSummary/toggleMultipleUnsavedChanges', data);
          }
        })
      })
    }
  },
};
</script>

<style lang="scss">
@import "../../../../../scss/colors";
</style>
